import { useState, useEffect } from 'react';
import { isEqual } from 'lodash';
import { Formik, Form } from 'formik';
import Yup from '../../utils/yup';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { NotificationType } from '../../config';
import addNotification from '../../utils/addNotification';

import useUsers from '../../context/UsersContext';
import HistoryDialog from '../History/HistoryDialog';
import { getDynVals } from '../../utils/DynHelper';
import projectService from '../../service/project.service';
import { cleanValues, generateSubItems, getJokerFieldType } from '../../utils/helper';
import { DeleteDialogUI, FormButtonPanelUI, FormHeaderUI } from '../Interface';
import { ProjectFields } from './ProjectFields';
import History from '../History/History';
import ProjectTabs from './ProjectTabs';
import { Dependence } from '../../config/taskList';
import propertyService from '../../service/property.service';
import { useParams } from 'react-router-dom';
import { useLoader } from '../../provider/LoaderProvider';
import CompanyDialog from '../Company/CompanyDialog';
import masterDataService from '../../service/masterData.service';
import { JokerFieldTypes, JokerTypes } from '../../config/joker';
import { ProjectPriceDirection } from '../../config/project';

export const ProjectPage = ({ projectState, infoPanel, dialog, fromDialog, dialogSetSelectedProject }) => {
  const {
    setRefetchTable,
    selectedValues,
    setSelectedValues,
    viewField,
    setViewField,
    operatorsDisabled,
    setOperatorsDisabled,
    submenu,
    submenuNames,
    params,
    canEdit,
    canCopy,
    canDelete,
    setRowSelectionModel,
    dynProps,
    setDynProps,
    autoCompleteOptions,
    dynLoading,
    setDynLoading,
    companyContactsState,
    def,
    autocompleteData,
    setAutoCompleteOptions,
    setAutocompleteData,
    isLoading,
    jokerFields,
    getJokerFields,
    jokerLoading,
  } = projectState;
  const { id } = useParams();
  const { showLoader, hideLoader } = useLoader();
  const { companyContacts, setCompanyContacts } = companyContactsState;
  const { user } = useUsers();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);

  const [defaultValues, setDefaultValues] = useState({});
  const [initValues, setInitValues] = useState({});
  const [defaultSelectedValues, setDefaultSelectedValues] = useState({});
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [propertyGroups, setPropertyGroups] = useState([]);
  const [group, setGroup] = useState({
    id: [],
  });
  const [projectStartDate, setProjectStartDate] = useState(dayjs(defaultValues.projectStartDate));
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);

  const defaultCommunication = {
    id: null,
    isNew: true,
    message: '',
    userId: user.userId,
    userName: user.userName,
    createdAt: null,
  };

  const defProjectPrice = {
    isNew: true,
    itemCode: '',
    direction: ProjectPriceDirection.OUTGOING,
    currencyCode: null,
    description: '',
  };

  const defProjectEmployee = {
    isNew: true,
    employeeId: null,
    leader: false,
    role: '',
  };

  const defaultTaskList = {
    id: null,
    isNew: true,
    name: '',
    duration: 1,
    statusId: autocompleteData?.taskListStatus?.defaultValue ?? null,
    responsiblePersonId: null,
    waitingTime: null,
    preparedness: null,
    dependence: Dependence.FS,
    startDate: null,
    createdBy: user.userId,
    visOrder: null,
    userGroupId: null,
    construction: true,
    userId: user?.userId,
    taskRatio: null,
  };

  const defaultContact = {
    id: null,
    isNew: true,
    name: '',
    role: '',
    phoneNumber: '',
    email: '',
    createdBy: user.userId,
  };

  const defaultResourceFive = {
    id: null,
    isNew: true,
    resourceId: '',
    createdBy: user.userId,
  };

  const defaultResourceFour = {
    id: null,
    isNew: true,
    resourceId: '',
    createdBy: user.userId,
  };

  const defaultResourceThree = {
    id: null,
    isNew: true,
    resourceId: '',
    createdBy: user.userId,
  };

  const defaultResourceTwo = {
    id: null,
    isNew: true,
    resourceId: '',
    createdBy: user.userId,
  };

  const defaultResourceOne = {
    id: null,
    isNew: true,
    resourceId: '',
    leader: false,
    educationDate: null,
    createdBy: user.userId,
    registered: false,
  };

  useEffect(() => {
    setDefaultValues(def);
    getJokerFields();
  }, []);

  useEffect(() => {
    if (fromDialog && Object.keys(selectedValues).length > 0) {
      dialogSetSelectedProject(selectedValues ?? null);
    }
  }, [selectedValues]);

  useEffect(() => {
    if (Object.keys(autocompleteData).length > 0) {
      setDefaultValues((prev) => {
        return {
          ...prev,
          statusId: autocompleteData?.status?.defaultValue ?? null,
          classificationId: autocompleteData?.classification?.defaultValue ?? null,
          ...(dialog?.dialogData && {
            ...dialog?.dialogData,
            dynamicFields: getDynVals(dynProps, dialog?.dialogData.dynamicProperties),
          }),
        };
      });

      setDefaultSelectedValues((prev) => {
        return {
          ...prev,
          status: autocompleteData?.status?.defItem,
          classification: autocompleteData?.classification?.defItem,
        };
      });
    }
  }, [autocompleteData]);

  useEffect(() => {
    if (submenu) {
      getPropertyGroupsForForm();
    }
  }, [submenu]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!isEqual(defaultSelectedValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
        setInitValues(defaultValues);
      }
    }
  }, [selectedValues]);

  useEffect(() => {
    setDefaultValues((prev) => {
      return { ...prev, userId: user.userId };
    });

    setDefaultSelectedValues((prev) => {
      return {
        ...prev,
        user: user,
      };
    });
  }, [user.userId]);

  useEffect(() => {
    if (id) {
      getProjectById(id);
      return;
    }

    if (!dialog?.isUpdate) {
      setSelectedValues(defaultSelectedValues);
    }
  }, [defaultSelectedValues, id, dialog]);

  const getProjectById = (projectId) => {
    showLoader();
    projectService
      .getProjectById(projectId)
      .then((data) => {
        setSelectedValues(data);
      })
      .finally(() => {
        hideLoader();
      });
  };

  const getPropertyGroupsForForm = () => {
    propertyService.getPropertyGroupsForSubmenus({ data: [submenu] }).then((data) => {
      setPropertyGroups(data);
    });
  };

  const validationSchema = Yup.object().shape({
    projectId: Yup.string().nullable(),
    projectName: Yup.string().required(),
    companyShortName: Yup.string().nullable(),
    companyId: Yup.string().nullable(),
    statusId: Yup.string().nullable(),
    projectStartDate: Yup.string().required(),
    projectEndDate: params.PROJECT_ENDDATE_NOT_REQUIRED === 'Y' ? Yup.string().nullable() : Yup.string().required(),
    projectPrepare: Yup.number().nullable().positive(),
    userId: Yup.string().required(),
    projectDescription: Yup.string().max(3000).nullable(),
    projectGroupId: Yup.string().nullable(),
    orderNumber: Yup.string().nullable(),
    active: Yup.string().nullable(),
    provinceId: Yup.string().nullable(),
    companyContactId: Yup.string().nullable(),
    projectTypeId: Yup.string().nullable(),
    classificationId: Yup.string().nullable(),
    projectTypeStatusId: Yup.string().nullable(),
    costCenterCode: Yup.string().nullable(),
    companyLocationId: Yup.string().nullable(),
    communication: Yup.array().of(
      Yup.object().shape({
        message: Yup.string().required(),
      })
    ),
    contact: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required(),
        role: Yup.string().required(),
        phoneNumber: Yup.string().nullable(),
        email: Yup.string().nullable(),
      })
    ),
    projectPrice: Yup.array().of(
      Yup.object().shape({
        itemCode: Yup.string().required(),
        direction: Yup.string().required(),
        currencyCode: Yup.string().required(),
        description: Yup.string().nullable(),
      })
    ),
    projectEmployee: Yup.array().of(
      Yup.object().shape({
        employeeId: Yup.string().required(),
        leader: Yup.boolean(),
        role: Yup.string().nullable(),
      })
    ),
    resourceFive: Yup.array().of(
      Yup.object().shape({
        resourceId: Yup.string().required(),
      })
    ),
    resourceFour: Yup.array().of(
      Yup.object().shape({
        resourceId: Yup.string().required(),
      })
    ),
    resourceThree: Yup.array().of(
      Yup.object().shape({
        resourceId: Yup.string().required(),
      })
    ),
    resourceTwo: Yup.array().of(
      Yup.object().shape({
        resourceId: Yup.string().required(),
      })
    ),
    resourceOne: Yup.array().of(
      Yup.object().shape({
        resourceId: Yup.string().required(),
        leader: Yup.string().nullable(),
        educationDate: Yup.string().nullable(),
        registered: Yup.boolean(),
      })
    ),
    ...(jokerFields.length > 0
      ? Object.fromEntries(
          jokerFields.map((field) => {
            const fieldType = getJokerFieldType(field);
            return [
              field.field,
              fieldType === JokerFieldTypes.NUMBER ? Yup.number().nullable() : Yup.string().nullable(),
            ];
          })
        )
      : {}),
  });

  const valuesToForm = () => {
    const {
      projectId,
      projectName,
      companyShortName,
      companyId,
      statusId,
      projectStartDate,
      projectEndDate,
      projectPrepare,
      userId,
      projectDescription,
      projectGroupId,
      orderNumber,
      active,
      provinceId,
      generatedName,
      projectCommunication,
      projectContact,
      projectResourceFive,
      projectResourceFour,
      projectResourceThree,
      projectResourceTwo,
      projectResourceOne,
      taskLists,
      companyContactId,
      projectTypeId,
      classificationId,
      projectTypeStatusId,
      costCenterCode,
      companyLocationId,
      joker_char1,
      joker_char2,
      joker_char3,
      joker_char4,
      joker_char5,
      joker_char6,
      joker_char7,
      joker_char8,
      joker_char9,
      joker_char10,
      joker_date1,
      joker_date2,
      joker_date3,
      joker_date4,
      joker_date5,
      joker_num1,
      joker_num2,
      joker_num3,
      joker_num4,
      joker_num5,
      joker_num6,
      joker_num7,
      joker_num8,
      joker_num9,
      joker_num10,
      joker_num11,
      joker_num12,
      joker_num13,
      joker_num14,
      joker_num15,
      joker_int1,
      joker_int2,
      joker_int3,
      joker_int4,
      joker_int5,
      projectPrice,
      projectEmployee,
    } = selectedValues;

    setInitValues({
      projectId,
      projectName,
      companyShortName: companyShortName ?? defaultValues.companyShortName,
      companyId,
      statusId,
      projectStartDate: dayjs(projectStartDate),
      projectEndDate: dayjs(projectEndDate),
      projectPrepare: projectPrepare ?? defaultValues.projectPrepare,
      userId,
      projectDescription: projectDescription ?? defaultValues.projectDescription,
      projectGroupId,
      companyContactId,
      projectTypeId,
      classificationId,
      projectTypeStatusId,
      costCenterCode,
      companyLocationId,
      orderNumber: orderNumber ?? defaultValues.orderNumber,
      active,
      provinceId,
      generatedName,
      dynamicFields: getDynVals(dynProps, selectedValues.dynamicProperties),
      joker_char1: joker_char1 ?? '',
      joker_char2: joker_char2 ?? '',
      joker_char3: joker_char3 ?? '',
      joker_char4: joker_char4 ?? '',
      joker_char5: joker_char5 ?? '',
      joker_char6: joker_char6 ?? '',
      joker_char7: joker_char7 ?? '',
      joker_char8: joker_char8 ?? '',
      joker_char9: joker_char9 ?? '',
      joker_char10: joker_char10 ?? '',
      joker_date1,
      joker_date2,
      joker_date3,
      joker_date4,
      joker_date5,
      joker_num1: joker_num1 ?? '',
      joker_num2: joker_num2 ?? '',
      joker_num3: joker_num3 ?? '',
      joker_num4: joker_num4 ?? '',
      joker_num5: joker_num5 ?? '',
      joker_num6: joker_num6 ?? '',
      joker_num7: joker_num7 ?? '',
      joker_num8: joker_num8 ?? '',
      joker_num9: joker_num9 ?? '',
      joker_num10: joker_num10 ?? '',
      joker_num11: joker_num11 ?? '',
      joker_num12: joker_num12 ?? '',
      joker_num13: joker_num13 ?? '',
      joker_num14: joker_num14 ?? '',
      joker_num15: joker_num15 ?? '',
      joker_int1: joker_int1 ?? '',
      joker_int2: joker_int2 ?? '',
      joker_int3: joker_int3 ?? '',
      joker_int4: joker_int4 ?? '',
      joker_int5: joker_int5 ?? '',
      communication:
        projectCommunication &&
        projectCommunication.map((rc) => ({
          id: rc.id,
          message: rc.message,
          userId: rc.user.userId,
          userName: rc.user.userName,
          createdAt: rc.createdAt,
        })),
      contact:
        projectContact &&
        projectContact.map((pc) => ({
          id: pc.id,
          name: pc.name,
          role: pc.role,
          phoneNumber: pc.phoneNumber,
          email: pc.email,
        })),
      projectPrice:
        projectPrice &&
        projectPrice.map((pp) => ({
          id: pp.id,
          itemCode: pp.itemCode,
          direction: pp.direction,
          currencyCode: pp.currencyCode,
          description: pp.description,
          price: pp.price,
          validFrom: pp.validFrom,
          validTo: pp.validTo,
          projectId: pp.projectId,
        })),
      projectEmployee:
        projectEmployee &&
        projectEmployee.map((pe) => ({
          id: pe.id,
          employeeId: pe.employeeId,
          leader: pe.leader,
          role: pe.role,
        })),
      resourceFive:
        projectResourceFive &&
        projectResourceFive.map((pr) => ({
          id: pr.listId,
          resourceId: pr.resourceId,
        })),
      resourceFour:
        projectResourceFour &&
        projectResourceFour.map((pr) => ({
          id: pr.listId,
          resourceId: pr.resourceId,
        })),
      resourceThree:
        projectResourceThree &&
        projectResourceThree.map((pr) => ({
          id: pr.listId,
          resourceId: pr.resourceId,
        })),
      resourceTwo:
        projectResourceTwo &&
        projectResourceTwo.map((pr) => ({
          id: pr.listId,
          resourceId: pr.resourceId,
        })),
      resourceOne:
        projectResourceOne &&
        projectResourceOne.map((pr) => ({
          id: pr.listId,
          resourceId: pr.resourceId,
          leader: pr.leader,
          educationDate: pr.educationDate,
          simpleTask: pr.simpleTask,
          registered: pr.registered,
          role: pr.projectResourceOneRole[0]?.itemCode,
        })),
      taskList:
        taskLists &&
        taskLists.map((tL) => ({
          id: tL.id,
          name: tL.name,
          statusId: tL.statusId,
          responsiblePersonId: tL.responsiblePersonId,
          responsibleUserGroupId: tL.responsibleUserGroupId,
          responsibleCompanyId: tL.responsibleCompanyId,
          responsibleWorkerId: tL.responsibleWorkerId,
          responsibleType: tL.responsibleType,
          duration: tL.duration,
          startDate: tL.startDate,
          visOrder: tL.visOrder,
          preparedness: tL.preparedness,
          dependence: tL.dependence,
          parentId: tL.parentId,
          path: tL.path,
          userGroupId: tL.userGroupId,
          typeId: tL.typeId,
          description: tL.description,
          taskListAttachment: tL?.taskListAttachment,
          taskListReport: tL?.taskListReport,
          isVirtual: tL.isVirtual,
          deadLine: tL.deadLine,
          construction: tL.construction,
          waitingTime: tL.waitingTime,
          userId: tL.userId,
          taskRatio: tL.taskRatio,
          taskListRespPerson: tL?.taskListRespPerson?.map((respPerson) => ({
            id: respPerson.id,
            userId: respPerson.userId,
          })),
        })),
    });
  };

  const handleSubmit = (values) => {
    const projectResourceOne = values.resourceOne || [];
    const projectResourceTwo = values.resourceTwo || [];

    if (params.PROJECT_WARNINGS === 'Y') {
      if (projectResourceOne.length > 0) {
        const typeFourCount = projectResourceOne.filter(
          (resource) => resource.resourceOne?.type?.itemName === '4'
        ).length;

        if (typeFourCount < 2) {
          addNotification({
            content: 'A projekt nem tartalmaz két 4-es típusú dolgozót!',
            type: NotificationType.WARNING,
          });
        }
      }

      if (projectResourceOne.length > 0 && projectResourceTwo.length === 0) {
        addNotification({
          content: 'A projekt nem tartalmaz autókat!',
          type: NotificationType.WARNING,
        });
      }
    }

    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField || !dialog.isUpdate) {
      handleInsertObject(values);
    }
  };

  const updateObject = async (values) => {
    const generatedValues = generateSubItems(values.taskList);

    const updData = {
      ...cleanValues(values),
      projectPrepare: parseInt(values.projectPrepare),
      projectStartDate: dayjs(values.projectStartDate).startOf('day').add(12, 'hours').toISOString(),
      projectEndDate: dayjs(values.projectEndDate).isValid()
        ? dayjs(values.projectEndDate).startOf('day').add(12, 'hours').toISOString()
        : null,
      updatedBy: user.userId,
      taskList: generatedValues,
    };

    projectService.updateProject(updData, selectedValues.projectId).then((data) => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });

      setSelectedValues(data);
      setRefetchTable(true);
      setEditButtonvisible(true);
      setViewField(true);

      if (dialog) {
        dialog.setTaskChanged(true);
        dialog.handleClose();
      }
    });
  };

  const handleInsertObject = async (values) => {
    const generatedValues = generateSubItems(values.taskList);

    const insertObject = {
      ...cleanValues(values),
      projectPrepare: parseInt(values.projectPrepare),
      projectStartDate: dayjs(values.projectStartDate).startOf('day').add(12, 'hours').toISOString(),
      projectEndDate: dayjs(values.projectEndDate).isValid()
        ? dayjs(values.projectEndDate).startOf('day').add(12, 'hours').toISOString()
        : null,
      createdBy: user.userId,
      taskList: generatedValues,
    };

    projectService
      .createProject(insertObject)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });

        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
        setSelectedValues(data);

        setRowSelectionModel(data.projectId);

        if (dialog) {
          dialog.setTaskChanged(true);
          dialog.handleClose();
        }
      })
      .finally(() => {
        //@TODO loader
      });
  };

  dayjs.extend(utc);

  const handleRemoveElement = async () => {
    const deleteData = {
      deletedBy: user.userId,
    };
    projectService.deleteProject(deleteData, selectedValues.projectId).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues(defaultValues);
    });
  };

  const onAddClick = () => {
    setSelectedValues(defaultSelectedValues);
    setViewField(false);
    setAddField(false);
  };

  const onModifyClick = () => {
    getProjectById(selectedValues.projectId);
    setViewField(false);
    setEditButtonvisible(false);
  };

  const onDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const onCopyClick = () => {
    setViewField(false);
    setAddField(false);
  };

  const handleTemplate = () => {
    projectService.getExcelTemplate().then(() => {
      addNotification({
        content: 'A letöltés elindult!',
        type: NotificationType.SUCCESS,
      });
    });
  };

  const handleExport = () => {
    projectService.getExcelData().then(() => {
      addNotification({
        content: 'A letöltés elindult!',
        type: NotificationType.SUCCESS,
      });
    });
  };

  const handleImport = (files) => {
    projectService.importExcel(files, user.userId).then(() => {
      setRefetchTable(true);
      addNotification({
        content: 'Az import sikeres!',
        type: NotificationType.SUCCESS,
      });
    });
  };

  const selectCompanyFromDialog = async (companyId, setFieldValue) => {
    setCompanyDialogOpen(false);

    if (!companyId) return;

    const findedCompany = autocompleteData.company.find((i) => i.id === companyId);

    if (!findedCompany) {
      const company = await masterDataService.companyToFormById(companyId);

      if (!company) {
        addNotification({
          content: 'Hiba történt az Üzleti partner adatainak lekérésekor!',
          type: NotificationType.ERROR,
        });
      }

      const newAutocompleteData = { ...autocompleteData };
      newAutocompleteData.company.push(company);
      setAutocompleteData(newAutocompleteData);

      const newAutocompleteOptions = { ...autoCompleteOptions };
      newAutocompleteOptions.company.push({ label: company.name, value: company.id });
      setAutoCompleteOptions(newAutocompleteOptions);
    }

    setFieldValue('companyId', companyId);
  };

  return (
    <>
      <div className="h-full overflow-auto custom-scrollbar">
        <div className="flex justify-between items-center">
          <FormHeaderUI
            addClick={!infoPanel && !dialog ? onAddClick : null}
            addDisabled={!viewField || !canEdit}
            modClick={!infoPanel && (!dialog || dialog.isUpdate) ? onModifyClick : null}
            modDisabled={!viewField || operatorsDisabled || !canEdit}
            deleteClick={
              (typeof params.PROJECT_DELETE_BUTTON === 'undefined' || params.PROJECT_DELETE_BUTTON === 'Y') &&
              !infoPanel &&
              (!dialog || dialog.isUpdate)
                ? onDeleteClick
                : null
            }
            deleteDisabled={!viewField || operatorsDisabled || !canEdit || !canDelete}
            copyClick={!infoPanel && (!dialog || dialog.isUpdate) ? onCopyClick : null}
            copyDisabled={!viewField || operatorsDisabled || !canEdit || !canCopy}
          />
          <div>
            <History disabled={operatorsDisabled} onClick={() => setHistoryDialogOpen(!historyDialogOpen)} />
          </div>
        </div>

        <Formik
          initialValues={initValues}
          validationSchema={validationSchema}
          validateOnChange={false}
          onSubmit={handleSubmit}
          enableReinitialize
          //validateOnMount={false}
        >
          {({ values, setFieldValue, errors, validateField, resetForm }) => (
            <Form>
              <ProjectFields
                viewField={(!dialog || dialog?.isUpdate) && viewField}
                selectedValues={selectedValues}
                values={values}
                setFieldValue={setFieldValue}
                errors={errors}
                validateField={validateField}
                params={params}
                autoCompleteOptions={autoCompleteOptions}
                setProjectStartDate={setProjectStartDate}
                selectedCompanyState={{ selectedCompany, setSelectedCompany }}
                companyContactsState={{ companyContacts, setCompanyContacts }}
                autocompleteData={autocompleteData}
                setCompanyDialogOpen={setCompanyDialogOpen}
              />
              {!jokerLoading && (
                <ProjectTabs
                  disabled={(!dialog || dialog?.isUpdate) && viewField}
                  submenuId={submenu}
                  addField={addField}
                  setFieldValue={setFieldValue}
                  validateField={validateField}
                  selectedValues={selectedValues}
                  setSelectedValues={setSelectedValues}
                  setRefetchTable={setRefetchTable}
                  user={user}
                  values={values}
                  errors={errors}
                  submenuNames={submenuNames}
                  params={params}
                  canEdit={canEdit}
                  setDefaultValues={setDefaultValues}
                  dynProperties={{ dynProps, setDynProps }}
                  infoPanel={infoPanel}
                  defaultField={defaultCommunication}
                  defaultContact={defaultContact}
                  defaultResourceFive={defaultResourceFive}
                  defaultResourceFour={defaultResourceFour}
                  defaultResourceThree={defaultResourceThree}
                  defaultResourceTwo={defaultResourceTwo}
                  defaultResourceOne={defaultResourceOne}
                  autoCompleteOptions={autoCompleteOptions}
                  checkLists={selectedValues?.checkLists ?? []}
                  defaultTaskList={defaultTaskList}
                  lineId={selectedValues.projectId}
                  dynLoadingState={{ dynLoading, setDynLoading }}
                  autocompleteData={autocompleteData}
                  propertyGroups={propertyGroups}
                  groupState={{ group, setGroup }}
                  projectStartDate={projectStartDate}
                  companyContacts={companyContacts}
                  selectedCompany={selectedCompany}
                  handleImport={handleImport}
                  handleExport={handleExport}
                  handleTemplate={handleTemplate}
                  jokerFields={jokerFields}
                  defProjectPrice={defProjectPrice}
                  defProjectEmployee={defProjectEmployee}
                />
              )}
              <CompanyDialog showDialog={companyDialogOpen} onClose={selectCompanyFromDialog} />

              <FormButtonPanelUI
                onCancelClick={() => {
                  setViewField(true);
                  setEditButtonvisible(true);
                  setAddField(true);
                  resetForm();

                  if (dialog) {
                    dialog.handleClose();
                  }
                }}
                disabled={
                  dialog
                    ? dialog.isUpdate && ((editButtonVisible && addField) || (!editButtonVisible && !addField))
                    : (editButtonVisible && addField) || (!editButtonVisible && !addField)
                }
              />
            </Form>
          )}
        </Formik>
      </div>
      <HistoryDialog
        open={historyDialogOpen}
        handleClose={() => {
          setHistoryDialogOpen(false);
        }}
        formId={submenu}
        lineId={selectedValues.projectId}
        fieldConfig={[
          { field: 'projectCommunication', objKey: 'message' },
          { field: 'dynamicProperties', objKey: 'textVal' },
        ]}
      />
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};
